import loadable from '@loadable/component';
import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import moment from 'moment';
import Numeral from 'numeral';
import { Component, CSSProperties, ReactElement } from 'react';
import { Trans } from 'react-i18next';
import { connect } from 'react-redux';
import { Navigate, Outlet } from 'react-router';
import { compose } from 'redux';
import { Spinner } from '../../../../../../shared/frontend/components/presentational/spinner';
import history from '../../../../../../shared/frontend/libs/history';
import { areFieldsDifferent, naturalShortDate, relativeDate } from '../../../../../../shared/frontend/libs/utils';
import { TError, createActionSetError } from '../../../../../../shared/frontend/scripts/actions/errorActions';
import { HelmetWithTitleAndDescription } from '../../../../../../shared/frontend/components/helmetHelper';
import { FACTIONS } from '../../../../shared/enums';
import { RouteComponentProps } from '../../../../../../shared/common/types/common';
import { createActionGetGuildDetails } from '../../actions/getGuildDetails';
import { createActionSetGuildPageLoading } from '../../actions/setGuildPageLoading';
import { resolveRoutePath } from '../../bundles/routes';
import EmbeddedYoutubeVideoPlaylist from '../../components/presentational/EmbeddedYoutubeVideoPlaylist';
import { TwoPaneContent } from '../../../../../../shared/frontend/components/presentational/layouts';
import { TrackedLink } from '../../../../../../shared/frontend/components/link';
import QueueButton from '../../components/presentational/queueButton';
import { ProgressBadge } from '../../components/presentational/raidRankingsElements/ProgressBadge';
import { SimpleTooltip, SimpleTooltip_NEXT } from '../../../../../../shared/frontend/components/presentational/tooltip';
import { ZoneContainer } from '../../../../../../shared/frontend/components/ads/zone';
import frontendLog from '../../libs/frontendLog';
import { TApplicationState } from '../../libs/store';
import apiService, { TGetGuildDetailsParams } from '../../services/apiService';
import { RosterHistoryTable } from '../../shared/activity/components/RosterHistoryTable';
import { GuildMasthead, GuildSubNav } from '../components/guildComponents';
import { SiteBackgroundOverride } from '../../../../../../shared/frontend/components/siteBackgroundOverride';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { getPageBackdropAssetPath } from '../../../../../../shared/common/customizations';
import { actions as RecruitmentPageActions } from '../modules';
import SkeletonGuildPageContainer from '../../../../../../shared/frontend/components/guild/SkeletonGuildPageContainer';
import { Expansions } from '../../../../../../shared/common/enums';
import { getRaidWeekForRaidAndRegion } from '../../../../../../shared/common/utils';
import type * as GuildAdminToolsModule from '../../../../../../shared/frontend/components/guildAdminTools';
import { Button } from '../../../../../../shared/frontend/components/presentational/buttons/Button';
import { withRouter } from '../../../../../../shared/frontend/libs/router/router-helpers';
import { isHexColor, legibleOnDark } from '../../../../../../shared/frontend/libs/colors';

const GuildAdminTools = loadable(() => import('../../../../../../shared/frontend/components/guildAdminTools'), {
  resolveComponent: (module: typeof GuildAdminToolsModule) => module.GuildAdminTools,
  fallback: null
});

type TGuildPageContainerClientState = any;
type TGuildPageContainerServerState = any;

type TPropsFromState = TGuildPageContainerClientState & { isFullProfile: boolean };

type TPropsThatDispatch = {
  setGuildPageContainer: (actionData: TGuildPageContainerClientState) => void;
  setGuildPageLoading: (loading: boolean) => void;
  showError: (actionData: TError) => void;
  loadGuildRecruitmentProfiles: () => void;
};

export type TGuildPageContainerProps = TPropsFromState & TPropsThatDispatch & RouteComponentProps<any, any>;

export function buildRaidHighlights(props: any) {
  let matchedCount = 0;
  return _.compact(
    _.flatten(
      props.raids.map((raid: any, index: number) => {
        const matchedRaidProgress = props.raidProgress.filter((r: any) => r.raid === raid.slug);
        if (matchedRaidProgress.length === 0) {
          // no progress for this raid
          return [];
        }

        // only refer to current expansion raids
        if (raid.expansion_id !== Expansions.Current) {
          return [];
        }

        const difficulties = [];
        const [raidProgress] = matchedRaidProgress;

        for (const diff of ['mythic', 'heroic', 'normal']) {
          if (raidProgress.encountersDefeated[diff].length > 0) {
            difficulties.push(diff);
          }
        }

        if (difficulties.length === 0) {
          // has not killed anything for this raid
          return [];
        }

        if (difficulties.length > 1) {
          if (raidProgress.encountersDefeated[difficulties[0]].length === raid.encounters.length) {
            difficulties.length = 1;
          }
        }

        const raidWeekAotC = getRaidWeekForRaidAndRegion(
          raid.slug,
          props.guild.region.slug,
          'heroic',
          moment.utc(raidProgress.aotc)
        );

        const raidWeekCuttingEdge = getRaidWeekForRaidAndRegion(
          raid.slug,
          props.guild.region.slug,
          'mythic',
          moment.utc(raidProgress.cuttingEdge)
        );

        // show the top 2 difficulties only
        return difficulties.slice(0, 2).map((difficulty: string) => (
          <div
            key={`${difficulty}-${raid.slug}`}
            className={cx('slds-col slds-text-align--center', matchedCount++ >= 2 ? 'rio-hidden--small' : '')}
          >
            <div className="d-inline-block" style={{ maxWidth: '10rem' }}>
              <ProgressBadge
                numDefeated={raidProgress.encountersDefeated[difficulty].length}
                totalBosses={raid.encounters.length}
                difficulty={difficulty}
                visible={true}
                color="light"
                size="medium"
              />
              <div
                className="slds-text-heading--small text-white slds-m-top--small rio-text-shadow--normal"
                style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
              >
                {t(`raids:${raid.slug}.short-name`)}
              </div>
              {
                <div>
                  {raidProgress.aotc && difficulty === 'heroic' && (
                    <SimpleTooltip
                      placement="bottom"
                      tooltip={
                        <span>
                          <Trans
                            i18nKey="guilds:cleared-difficulty"
                            values={{
                              number: Numeral(raidWeekAotC).format('O,o'),
                              difficulty: t('common:difficulty.heroic.name')
                            }}
                          >
                            <b className="text-epic" />
                            <br />
                          </Trans>
                        </span>
                      }
                    >
                      <div className="rio-text-shadow--normal text-epic">[AOTC]</div>
                    </SimpleTooltip>
                  )}

                  {difficulty !== 'mythic' && !raidProgress.aotc && <span>&nbsp;</span>}

                  {raidProgress.cuttingEdge && difficulty === 'mythic' && (
                    <SimpleTooltip
                      placement="bottom"
                      tooltip={
                        <span>
                          <Trans
                            i18nKey="guilds:cleared-difficulty"
                            values={{
                              number: Numeral(raidWeekCuttingEdge).format('O,o'),
                              difficulty: t('common:difficulty.mythic.name')
                            }}
                          >
                            <b className="text-legendary" />
                            <br />
                          </Trans>
                        </span>
                      }
                    >
                      <div className="rio-text-shadow--normal text-legendary">[CE]</div>
                    </SimpleTooltip>
                  )}

                  {difficulty === 'mythic' && !raidProgress.cuttingEdge && <span>&nbsp;</span>}
                </div>
              }
            </div>
          </div>
        ));
      })
    )
  );
}

type TGuildDetailsWrapperProps = {
  guild: any;
  raidRankings: any;
  raidProgress: any;
  raids: any;
  guildCustomizations: any;
  isFullProfile: boolean;
  group: any;
  meta: { missingAt?: string; lastCrawledAt: string };
  children: any;
  match: any;
  streamers: any;
  recruitmentProfiles: any;
  currentFeatures: any;
};

function GuildDetailsWrapper(props: TGuildDetailsWrapperProps) {
  const { guild, raidRankings, raidProgress, guildCustomizations, isFullProfile, group, meta, raids } = props;

  const guildSubNavProps = {
    guild,
    match: props.match,
    streamers: props.streamers, // this is coming from state.guildDetails
    recruitmentProfiles: props.recruitmentProfiles // this is coming from state.guildDetails
  };

  if (!guild || !raidRankings) {
    return <SkeletonGuildPageContainer />;
  }

  const statBlurbs = [];
  if (raidProgress && raidProgress.length > 0) {
    for (const raidProg of raidProgress) {
      const [raid] = raids.filter((r: any) => r.slug === raidProg.raid);

      if (!raid) {
        continue;
      }

      // only refer to current expansion raids
      if (raid.expansion_id !== Expansions.Current) {
        continue;
      }

      const prog = [];
      for (const difficulty of ['mythic', 'heroic', 'normal']) {
        const numDefeated = raidProg.encountersDefeated[difficulty].length;
        if (numDefeated === 0) {
          continue;
        }
        prog.push(`${numDefeated}/${raid.encounters.length} ${_.capitalize(difficulty)}`);
        if (numDefeated === raid.encounters.length) {
          break;
        }

        break;
      }
      if (prog.length > 0) {
        statBlurbs.push(`${raid.name} ${t('common:terms.progress')} ${prog.join(' + ')}`);
      }
    }
  }

  const faction = FACTIONS.find((f) => f.slug === guild.faction);

  let description = faction ? `${faction.name} ${t('common:terms.guild')}` : undefined;
  if (description && statBlurbs.length > 0) {
    description += `, ${statBlurbs.join(', ')}.`;
  }

  const raidHighlights = buildRaidHighlights(props);

  // Guild brand color: expose the raw hex (for borders/glows/washes) and a
  // luminance-floored variant (for text/edges that must stay visible) as CSS
  // custom properties. They cascade to the masthead, the guild name, and the
  // Renown card. Unset for colorless guilds, so every consumer falls back to
  // today's faction/gold tokens with zero visual change.
  const hasBrandColor = isHexColor(guild?.color);
  const guildAccentStyle: CSSProperties | undefined = hasBrandColor
    ? ({
        '--rio-guild-accent': guild.color,
        '--rio-guild-accent-legible': legibleOnDark(guild.color)
      } as CSSProperties)
    : undefined;

  return (
    <div style={guildAccentStyle}>
      <HelmetWithTitleAndDescription
        title={`‹${guild.name}› @ ${guild.realm.name} (${guild.region.short_name}) - ${t('guilds:guild-profile')}`}
        description={description}
      />
      {guildCustomizations?.page_backdrop && (
        <SiteBackgroundOverride imageUrl={asset(getPageBackdropAssetPath(guildCustomizations.page_backdrop))} />
      )}
      <GuildMasthead
        guild={guild}
        highlights={raidHighlights.splice(0, 3)}
        banner_asset_name={guildCustomizations ? guildCustomizations.profile_banner : ''}
        isAnimated={guildCustomizations ? guildCustomizations.use_animated_banner : false}
      />

      {/*
        min-height absorbs #main-content-container's 100vh CLS reservation so short
        guild pages don't show a naked backdrop band between this pane and the
        footer. The offset matches the chrome above the pane at its most compact;
        taller mastheads just overshoot, which only nudges the footer down.
      */}
      <div style={{ backgroundColor: '#4f4b49', minHeight: 'calc(100vh - 180px)' }}>
        {/*
          rio-guild-content-pane pins the content pane's TOP padding to a
          consistent 1rem at every breakpoint (see _base.scss). The default
          .rio-content-pane padding is responsive (1rem desktop / 0.5rem tablet /
          0 mobile), which left the sub-nav flush under the header on small
          screens — cramped, and colliding with the brand-color underglow. A
          consistent gap gives the buttons breathing room and lets the glow land
          in it uniformly across breakpoints.
        */}
        <TwoPaneContent className="rio-guild-content-pane">
          <div className="text-white">
            {!isFullProfile && (
              <div className="slds-text-align--center rio-alert rio-alert--warn slds-m-bottom--medium">
                <i className="fa fa-exclamation-triangle" /> &nbsp;
                <Trans i18nKey="guilds:chinese-limited-functionality">
                  <TrackedLink href="https://us.battle.net/forums/en/bnet/topic/20747737113#post-9" target="_blank" />
                </Trans>
              </div>
            )}

            {group && <GuildSubNav className="slds-m-bottom--small slds-scrollable--x" {...guildSubNavProps} />}

            {isFullProfile && !group && (
              <div className="slds-text-align--center rio-alert rio-alert--info slds-m-bottom--medium">
                {t('guilds:update-guild-access')}
                <QueueButton
                  component={
                    <Button className="slds-m-around--small" variant="primary">
                      Update
                    </Button>
                  }
                />
              </div>
            )}

            {isFullProfile && !meta.missingAt && (
              <section className="rio-hidden--x-large slds-text-align--center text-white slds-m-bottom--small">
                <div className="slds-grid slds-wrap">
                  <div className="slds-size--1-of-1 slds-medium-size--3-of-4">
                    <p className="rio-panel--alert bare">
                      <b style={{ color: '#f00' }}>
                        <i className="fa fa-exclamation-triangle" /> {t('guilds:important')}:
                      </b>{' '}
                      <Trans i18nKey="guilds:first-kill">
                        <b />
                        <b className="text-raiderio" />
                      </Trans>
                    </p>
                  </div>

                  <div className="slds-size--1-of-1 slds-medium-size--1-of-4">
                    <QueueButton
                      component={
                        <Button className="slds-m-around--small" variant="primary">
                          {t('guilds:update-guild-now')}
                        </Button>
                      }
                    />
                    <h3 className="text-muted">
                      {t('guilds:last-scanned', { date: relativeDate(meta.lastCrawledAt) })}
                    </h3>
                  </div>
                </div>
              </section>
            )}

            {meta.missingAt && (
              <div className="rio-panel--alert slds-m-bottom--medium">
                <div className="slds-text-heading--small text-warn text-bold slds-text-align--center">
                  <i className="fa fa-exclamation-triangle slds-m-right--xx-small" />
                  <Trans i18nKey="guilds:guild-missing-api" values={{ value: relativeDate(meta.missingAt) }}>
                    <SimpleTooltip_NEXT tooltip={naturalShortDate(meta.missingAt)} />
                  </Trans>
                </div>
              </div>
            )}

            {props.children}

            <div className="text-muted rio-text-body--xxx-small slds-text-align--right slds-m-top--x-small">
              <div className="rio-spaced-element--medium slds-show--inline-block">
                <span className="rio-no-select">RealmID</span> {guild.realm.id}
              </div>
              {group && (
                <div className="rio-spaced-element--medium slds-show--inline-block">
                  <span className="rio-no-select">GroupID</span> {group.id}
                </div>
              )}
              <div className="rio-spaced-element--medium slds-show--inline-block">
                <span className="rio-no-select">GuildID</span> {guild.id}
              </div>
            </div>
          </div>
          <div>
            {(props.currentFeatures.admin || props.currentFeatures.support_team) && (
              <GuildAdminTools guild={guild} group={group} />
            )}

            {props.currentFeatures.params.embedded_youtube_playlist_id && (
              <EmbeddedYoutubeVideoPlaylist playListId={props.currentFeatures.params.embedded_youtube_playlist_id} />
            )}

            <ZoneContainer tag="section" className="slds-text-align--center" type="mrec_sidebar_top_short" />
            {isFullProfile && (
              <section className="slds-text-align--center text-white">
                {!meta.missingAt && (
                  <>
                    <div className="rio-panel--alert bare">
                      <p>
                        <b style={{ color: '#f00' }}>
                          <i className="fa fa-exclamation-triangle" /> {t('guilds:important')}:
                        </b>{' '}
                        <Trans i18nKey="guilds:first-kill">
                          <b />
                          <b className="text-raiderio" />
                        </Trans>
                      </p>
                    </div>
                    <QueueButton
                      component={
                        <Button variant="primary" className="slds-m-around--small">
                          {t('guilds:update-guild-now')}
                        </Button>
                      }
                    />
                    <h3 className="text-muted">
                      {t('guilds:last-scanned', { date: relativeDate(meta.lastCrawledAt) })}
                    </h3>
                  </>
                )}
              </section>
            )}

            {props.currentFeatures.guild_roster_history_view && group && (
              <RosterHistoryTable group={group} type="sidebar" />
            )}

            <ZoneContainer tag="section" className="slds-text-align--center" sticky={true} type="mrec_sidebar_bottom" />
            {/*
              <div className="slds-text-align--center text-white slds-p-around--small">
                <h3>First Scanned</h3>
                {dateOnly(props.meta.firstCrawledAt)}
              </div>
              */}
          </div>
        </TwoPaneContent>
      </div>
    </div>
  );
}

/**
 * GuildPageContainer
 */
class GuildPageContainer extends Component<any, any> {
  public render(): ReactElement<TGuildPageContainerProps> {
    const children = this.props.children || <Outlet />;

    if (!this.props.match || !this.props.match.params.region) {
      return <div />;
    }

    if (this.props.siteConfig.isEmbedded) {
      return <>{children}</>;
    }

    if (!this.props.location || !this.props.location.pathname) {
      return <div />;
    }

    if (!this.props.guild || this.props.isLoading) {
      return <SkeletonGuildPageContainer />;
    }

    if (
      this.props.match.params.region !== this.props.guild.region.slug ||
      this.props.match.params.realm !== this.props.guild.realm.slug ||
      this.props.match.params.guild !== this.props.guild.name
    ) {
      // they are on the wrong capitalization or spelling of something,
      // so redirect to the proper one
      return (
        <Navigate
          to={resolveRoutePath(this.props.match.path, {
            region: this.props.guild.region.slug,
            realm: this.props.guild.realm.slug,
            guild: this.props.guild.name
          })}
        />
      );
    }

    return (
      <div style={{ position: 'relative' }}>
        {this.props.isLoading && <Spinner />}
        <GuildDetailsWrapper
          guild={this.props.guild}
          raidRankings={this.props.raidRankings}
          raidProgress={this.props.raidProgress}
          raids={this.props.raids}
          guildCustomizations={this.props.guildCustomizations}
          isFullProfile={this.props.isFullProfile}
          group={this.props.group}
          match={this.props.match}
          streamers={this.props.streamers}
          meta={this.props.meta}
          recruitmentProfiles={this.props.recruitmentProfiles}
          currentFeatures={this.props.currentFeatures}
        >
          {children}
        </GuildDetailsWrapper>
      </div>
    );
  }
}

// take state from store and convert into props
function mapStateToProps(state: TApplicationState, ownProps: any): TPropsFromState {
  return {
    ...state.guildDetails,
    isFullProfile: true,
    raids: state.siteMeta!.raids,
    siteConfig: state.siteConfig,
    location: state.router.location,
    currentFeatures: state.currentFeatures
  };
}

// return props that need access to `dispatch` function (props that can modify the store?)
function mapDispatchToProps(dispatch: Function, ownProps: TGuildPageContainerProps): TPropsThatDispatch {
  return {
    setGuildPageContainer(details: TGuildPageContainerClientState) {
      dispatch(createActionGetGuildDetails(details));
    },
    setGuildPageLoading(loading: boolean) {
      dispatch(createActionSetGuildPageLoading(loading));
    },
    showError(actionData: TError) {
      dispatch(createActionSetError(actionData));
    },

    loadGuildRecruitmentProfiles() {
      dispatch(RecruitmentPageActions.loadGuildRecruitmentProfiles());
    }
  };
}

// return one entire set of props
function mergeProps(stateProps: TPropsFromState, dispatchProps: TPropsThatDispatch, ownProps: any) {
  const loadParams: TGetGuildDetailsParams = {
    region: ownProps.match.params.region,
    realm: ownProps.match.params.realm,
    guild: decodeURIComponent(ownProps.match.params.guild)
  };

  const guildProps = stateProps.guild
    ? {
        region: stateProps.guild.region.slug,
        realm: stateProps.guild.realm.slug,
        guild: decodeURIComponent(stateProps.guild.name)
      }
    : {};

  const checkFields = ['region', 'realm', 'guild'];
  const guildChanged = !stateProps.guild || areFieldsDifferent(checkFields, loadParams, guildProps);

  if (
    !stateProps.isLoading &&
    guildChanged &&
    (stateProps.location.pathname === '/' || // hack: SSR loads will have react router location as / for some reason
      stateProps.location.pathname === ownProps.location.pathname)
  ) {
    dispatchProps.setGuildPageLoading(true);

    apiService.getGuildDetails(loadParams, async (err: any, result?: TGuildPageContainerServerState) => {
      if (err) {
        dispatchProps.setGuildPageLoading(false);

        frontendLog.error({ err }, 'getGuildDetails failed');

        if (err.statusCode === 400) {
          history.push('/');
          return;
        }

        dispatchProps.showError({ title: t('common:errors.something-went-wrong'), body: err.toString() });

        return;
      }

      if (!result) {
        dispatchProps.setGuildPageLoading(false);

        dispatchProps.showError({
          title: t('common:errors.something-went-wrong'),
          body: t('common:errors.unable-to-load-data-reload')
        });
        frontendLog.error('getGuildDetails empty');
        return;
      }

      dispatchProps.setGuildPageContainer({ ...result.guildDetails });
      dispatchProps.setGuildPageLoading(false);

      if (ownProps?.location?.pathname?.match('recruitment')) {
        dispatchProps.loadGuildRecruitmentProfiles();
      }
    });
  }

  return { ...ownProps, ...stateProps, ...dispatchProps };
}

export default compose(
  withRouter,
  connect<TPropsFromState, TPropsThatDispatch, TGuildPageContainerProps>(
    mapStateToProps as any,
    mapDispatchToProps,
    mergeProps as any
  )
)(GuildPageContainer);
